import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/hooks/queryKeys";
import { useUiStore } from "@/stores/ui.store";
import { Typography } from "@/components/ui/Typography";

interface TenantDetail {
  id: string;
  roomId: string;
  unitId: string;
  name: string;
  phone: string;
  email: string | null;
  agreementEndDate: string | null;
  moveInDate: string | null;
  noticePeriodDays: number;
  unitAlias?: string;
  unitNumber?: string;
}

export function EditTenantPage() {
  const params = useParams();
  const navigate = useNavigate();
  const propertyId = params.id as string;
  const unitId = params.unitId as string;
  const tenantId = params.tenantId as string;
  const queryClient = useQueryClient();
  const addToast = useUiStore((s) => s.addToast);

  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [endingAgreement, setEndingAgreement] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    agreementEndDate: "",
    moveInDate: "",
  });

  useEffect(() => {
    api
      .get<TenantDetail>(`/tenants/${tenantId}`)
      .then((t) => {
        setTenant(t);
        setForm({
          name: t.name,
          phone: t.phone,
          email: t.email ?? "",
          agreementEndDate: t.agreementEndDate ? t.agreementEndDate.slice(0, 10) : "",
          moveInDate: t.moveInDate ? t.moveInDate.slice(0, 10) : "",
        });
      })
      .catch(() => setError("Tenant not found"))
      .finally(() => setLoading(false));
  }, [tenantId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!tenant) return;
    setSubmitting(true);
    try {
      await api.put(`/tenants/${tenantId}`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        agreementEndDate: form.agreementEndDate || null,
        moveInDate: form.moveInDate || null,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.unitDetail(unitId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyDetail(propertyId) });
      addToast({ type: "success", message: "Tenant updated" });
      navigate(`/property/${propertyId}/unit/${unitId}`);
    } catch (err) {
      addToast({
        type: "error",
        message: err instanceof ApiError ? err.message : "Failed to update tenant",
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleEndAgreement() {
    if (!tenant) return;
    const today = new Date().toISOString().slice(0, 10);
    setEndingAgreement(true);
    try {
      await api.put(`/tenants/${tenantId}`, {
        name: tenant.name,
        phone: tenant.phone,
        email: tenant.email ?? null,
        agreementEndDate: today,
        moveInDate: form.moveInDate || null,
      });
      setForm((f) => ({ ...f, agreementEndDate: today }));
      queryClient.invalidateQueries({ queryKey: queryKeys.unitDetail(unitId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyDetail(propertyId) });
      addToast({ type: "success", message: "Agreement ended (end date set to today)" });
    } catch (err) {
      addToast({
        type: "error",
        message: err instanceof ApiError ? err.message : "Failed to update",
      });
    } finally {
      setEndingAgreement(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
        <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="space-y-6">
        <Link
          to={`/property/${propertyId}/unit/${unitId}`}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        >
          ← Back to unit
        </Link>
        <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6 text-center">
          <p className="text-red-700 dark:text-red-300 font-medium">{error ?? "Tenant not found"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to={`/property/${propertyId}/unit/${unitId}`}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
      >
        ← Back to unit
      </Link>
      <Typography variant="h1">Edit tenant</Typography>
      {tenant.unitAlias && (
        <p className="text-slate-500 dark:text-slate-400">
          {tenant.unitAlias} ({tenant.unitNumber})
        </p>
      )}

      <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/95 p-5 sm:p-6 space-y-4 max-w-lg">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Full name</label>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Phone</label>
          <input
            type="tel"
            required
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email (optional)</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
          />
        </div>
        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Agreement end date (optional)</label>
            <button
              type="button"
              onClick={handleEndAgreement}
              disabled={endingAgreement}
              className="text-sm font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 disabled:opacity-50"
            >
              {endingAgreement ? "Updating…" : "End agreement (set to today)"}
            </button>
          </div>
          <input
            type="date"
            value={form.agreementEndDate}
            onChange={(e) => setForm((f) => ({ ...f, agreementEndDate: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Move-in date (optional)</label>
          <input
            type="date"
            value={form.moveInDate}
            onChange={(e) => setForm((f) => ({ ...f, moveInDate: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
          />
        </div>
        <div className="flex gap-2 pt-2">
          <Link
            to={`/property/${propertyId}/unit/${unitId}`}
            className="rounded-lg border border-slate-200 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 dark:bg-primary-500 dark:hover:bg-primary-600"
          >
            {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
